import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Flex,
  GoodsCard,
  GoodsCardSkeletonList,
  List,
  useScreenSize,
} from '@banhala/ui-kit';
import { useFocusEffect } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { AnalyticsScreen } from 'shared/src/context';
import { bridge, useAsyncEffect, useNavigator } from 'shared/src/utils';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useMyInfo, useRemoveLikeGoods } from 'shared/src/queries';
import type { LikedGoodsPrimaryCardEntity } from 'shared/src/api';
import { useLikedGoods } from '../../queries';
import { likeGoodsFilterAtom } from '../../states/likeGoodsFilter.atom';
import { AiFittingButton } from './components/AiFittingButton';
import { CategoryFilterRow } from './components/CategoryFilterRow';
import { DeliveryFilterEmpty } from './components/DeliveryFilterEmpty';
import { LikedGoodsCountHeader } from './components/LikedGoodsCountHeader';
import { LikedGoodsEmpty } from './components/LikedGoodsEmpty';
import { LikedGoodsLoginBanner } from './components/LikedGoodsLoginBanner';
import { PrimaryCardItem } from './components/PrimaryCardItem';
import { AI_FITTING_ROOMS_ZZIM_DEEPLINK } from './constants/aiFittingRoomsZzim';
import { useLogClickEditOn } from './hooks/useLogClickEditOn';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogRefreshScreen } from './hooks/useLogRefreshScreen';
import { useLogViewLikedGoods } from './hooks/useLogViewLikedGoods';
import {
  convertLikedGoodsItemToGoodsData,
  type LikedGoodsCardItem,
} from './utils/convertLikedGoodsItemToGoodsData';

const SCREEN = 'LIKED_GOODS';

const COLUMN_GAP = 2;
const COLUMNS = 3;

const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

/** 3-col 그리드 슬롯 — 상품 또는 프로모션 카드 */
type GridSlot =
  | { type: 'goods'; entry: LikedGoodsCardItem }
  | { type: 'primary'; entity: LikedGoodsPrimaryCardEntity };

/**
 * 한 List에 헤더 + 상품을 함께 넣고 카테고리 필터만 sticky로 만들기 위한
 * row 단위 데이터.
 */
export type LikeGoodsRow =
  | { type: 'loginBanner'; key: 'loginBanner' }
  | { type: 'filter'; key: 'filter' }
  | { type: 'aiFitting'; key: 'aiFitting' }
  | { type: 'count'; key: 'count'; totalCount: number }
  | { type: 'empty'; key: 'empty'; reason: 'delivery' | 'no-likes' }
  | { type: 'gridRow'; key: string; slots: GridSlot[] };

interface LikeGoodsProps {
  isActive: boolean;
}

export const LikeGoods = ({ isActive }: LikeGoodsProps) => {
  return (
    <AnalyticsScreen screen={SCREEN}>
      <LikeGoodsContent isActive={isActive} />
    </AnalyticsScreen>
  );
};

const LikeGoodsContent = ({ isActive }: LikeGoodsProps) => {
  useLogViewLikedGoods();
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();
  const itemWidth = (screenWidth - COLUMN_GAP * (COLUMNS - 1)) / COLUMNS;

  const filter = useAtomValue(likeGoodsFilterAtom);
  const { data: myInfo } = useMyInfo();
  const isAnonymous = myInfo?.is_anonymous ?? false;
  const queryClient = useQueryClient();

  const filterParams = useMemo(
    () => ({
      is_fast_delivery: filter.isDeliveryFilterOn,
      category_sno: filter.selectedCategorySno ?? undefined,
      order: filter.sortKey ?? undefined,
    }),
    [filter.isDeliveryFilterOn, filter.selectedCategorySno, filter.sortKey],
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } =
    useLikedGoods(filterParams);

  const list = useMemo<LikedGoodsCardItem[]>(
    () => data?.item_list.map(convertLikedGoodsItemToGoodsData) ?? [],
    [data?.item_list],
  );

  // 상품 사이에 프로모션 카드를 정해진 index 위치에 인터리브
  const interleavedSlots = useMemo<GridSlot[]>(() => {
    const slots: GridSlot[] = list.map(entry => ({ type: 'goods', entry }));
    const primaries = data?.primary_card_list ?? [];
    // index 기준 정렬 후 앞에서부터 splice
    [...primaries]
      .sort((a, b) => a.index - b.index)
      .forEach(({ entity, index }) => {
        const insertAt = Math.min(Math.max(index, 0), slots.length);
        slots.splice(insertAt, 0, { type: 'primary', entity });
      });
    return slots;
  }, [list, data?.primary_card_list]);

  // RemoteConfig 미연동 — fallback 딥링크가 비어있지 않으면 노출 (iOS 동작과 매칭).
  const showAiFitting = AI_FITTING_ROOMS_ZZIM_DEEPLINK.length > 0;

  const rows = useMemo<LikeGoodsRow[]>(() => {
    const result: LikeGoodsRow[] = [];

    if (isAnonymous) {
      result.push({ type: 'loginBanner', key: 'loginBanner' });
    }

    const isEmpty = list.length === 0 && !isLoading && !isFetching;
    const hideFilters =
      isEmpty &&
      filter.selectedCategorySno == null &&
      !filter.isDeliveryFilterOn;

    if (!hideFilters) {
      result.push({ type: 'filter', key: 'filter' });
    }

    if (!isEmpty) {
      if (showAiFitting) {
        result.push({ type: 'aiFitting', key: 'aiFitting' });
      }
      result.push({
        type: 'count',
        key: 'count',
        totalCount: data?.total_count ?? 0,
      });
    }

    if (isEmpty) {
      result.push({
        type: 'empty',
        key: 'empty',
        reason: filter.isDeliveryFilterOn ? 'delivery' : 'no-likes',
      });
      return result;
    }

    for (let i = 0; i < interleavedSlots.length; i += COLUMNS) {
      const chunk = interleavedSlots.slice(i, i + COLUMNS);
      const firstKeyPart = chunk[0];
      const keySuffix =
        firstKeyPart.type === 'goods'
          ? `g-${firstKeyPart.entry.item.sno}`
          : `p-${i}`;
      result.push({ type: 'gridRow', key: `row-${keySuffix}`, slots: chunk });
    }
    return result;
  }, [
    interleavedSlots,
    list.length,
    data?.total_count,
    filter.selectedCategorySno,
    filter.isDeliveryFilterOn,
    isLoading,
    isFetching,
    isAnonymous,
    showAiFitting,
  ]);

  // useLikeMap의 initialLikeMap은 mount 시 한 번만 적용되므로,
  // list가 갱신될 때(다음 페이지/필터 변경)는 effect로 직접 setLikeMap 동기화.
  const { likeMap, setLikeMap } = useLikeMap();
  useEffect(() => {
    setLikeMap(prev => {
      const next = { ...prev };
      list.forEach(entry => {
        if (next[entry.item.sno] === undefined) {
          next[entry.item.sno] = entry.item.like?.is_liked ?? true;
        }
      });
      return next;
    });
  }, [list, setLikeMap]);

  useSyncLocalGoodsLike(setLikeMap);

  useAsyncEffect(async () => {
    if (!isActive) return;
    const localGoodsLike = await bridge.getLocalGoodsLike();
    setLikeMap(prev => ({ ...prev, ...localGoodsLike }));
  }, [isActive]);

  // 세션 변경(로그인/로그아웃) 시 likedGoods 캐시 무효화 → 재요청.
  // 첫 마운트는 제외하고 isAnonymous 변경된 케이스만.
  const prevAnonymousRef = useRef(isAnonymous);
  useEffect(() => {
    if (prevAnonymousRef.current === isAnonymous) return;
    prevAnonymousRef.current = isAnonymous;
    queryClient.invalidateQueries({ queryKey: ['likedGoods'] });
  }, [isAnonymous, queryClient]);

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: SCREEN,
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();

  const logClickGoods = useLogClickGoods();
  const logClickEditOn = useLogClickEditOn();

  // 필터 변경 + 화면 재진입(focus) 시 display 로그 중복 방지 Set 초기화.
  const [focusBump, setFocusBump] = useState(0);
  useFocusEffect(
    useCallback(() => {
      setFocusBump(b => b + 1);
    }, []),
  );
  const filterResetKey = `${filter.selectedCategorySno ?? 'all'}-${filter.sortKey}-${filter.isDeliveryFilterOn}-f${focusBump}`;
  const logDisplayGoods = useLogDisplayGoods({ resetKey: filterResetKey });

  const handlePressItem = (entry: LikedGoodsCardItem) => {
    logClickGoods(entry);
    const { item } = entry;
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: entry.logging.analytics,
    });
  };

  const handlePressLike = async (entry: LikedGoodsCardItem) => {
    const { item, logging } = entry;
    const isCurrentlyLiked = likeMap[item.sno] ?? false;
    if (isCurrentlyLiked) {
      await mutateRemoveLikeGoods({ sno: item.sno });
      bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
      setLikeMap(prev => ({ ...prev, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  const logRefreshScreen = useLogRefreshScreen();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    logRefreshScreen();
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [logRefreshScreen, refetch]);

  const showFooterSkeleton = hasNextPage || isLoading;

  return (
    <List<LikeGoodsRow>
      data={rows}
      keyExtractor={row => row.key}
      // 비회원 배너가 있으면 index 0이 loginBanner라 sticky 하지 않음.
      // 그 경우 index 1(=filter)이 sticky.
      stickyHeaderIndices={isAnonymous ? [1] : [0]}
      onEndReachedThreshold={1}
      onEndReached={() => {
        if (hasNextPage && !isFetching) fetchNextPage();
      }}
      refreshing={isRefreshing}
      onRefresh={handleRefresh}
      viewabilityConfig={VIEWABILITY_CONFIG}
      onViewableItemsChanged={logDisplayGoods}
      ListFooterComponent={
        showFooterSkeleton ? (
          <GoodsCardSkeletonList
            count={6}
            numColumns={COLUMNS}
            bottomContentHeight={80}
          />
        ) : null
      }
      renderItem={({ item: row }) => {
        if (row.type === 'loginBanner') return <LikedGoodsLoginBanner />;
        if (row.type === 'filter') {
          return <CategoryFilterRow disabled={isFetching} />;
        }
        if (row.type === 'aiFitting') return <AiFittingButton />;
        if (row.type === 'count') {
          return (
            <LikedGoodsCountHeader
              totalCount={row.totalCount}
              isLoading={isLoading}
              onPressEdit={() => {
                logClickEditOn();
                navigator.moveToEditGoodsLike({
                  categorySno: filter.selectedCategorySno,
                  isDeliveryFilterOn: filter.isDeliveryFilterOn,
                  order: filter.sortKey,
                });
              }}
            />
          );
        }
        if (row.type === 'empty') {
          return row.reason === 'delivery' ? (
            <DeliveryFilterEmpty />
          ) : (
            <LikedGoodsEmpty />
          );
        }
        if (row.type !== 'gridRow') return null;
        return (
          <Flex.Row gap={COLUMN_GAP}>
            {row.slots.map((slot, idx) => (
              <Flex
                key={
                  slot.type === 'goods'
                    ? `g-${slot.entry.item.sno}`
                    : `p-${idx}`
                }
                flex={1}
              >
                {slot.type === 'goods' ? (
                  <GoodsCard
                    data={slot.entry}
                    size={'medium'}
                    defaultWidth={itemWidth}
                    isLike={likeMap[slot.entry.item.sno]}
                    onPress={() => handlePressItem(slot.entry)}
                    onPressLike={async () => {
                      await handlePressLike(slot.entry);
                    }}
                  />
                ) : (
                  <PrimaryCardItem entity={slot.entity} width={itemWidth} />
                )}
              </Flex>
            ))}
            {Array.from({ length: COLUMNS - row.slots.length }).map((_, i) => (
              <Flex key={`pad-${i}`} flex={1} />
            ))}
          </Flex.Row>
        );
      }}
    />
  );
};
